import pwywDiscounts from './pwyw-discounts';
import { getCart, monitorCartChange, waitForUpCartShadowRoot } from './upcart-cart-observer';

/**
 * Pay What You Want (PWYW) bracelet PDP.
 *
 * There is no custom price and no per-amount variant: the bracelet sells at the base price and the
 * chosen amount is applied as a pre-created percentage code — 25 becomes PWYW_75, mapped in
 * pwyw-discounts.js. This module intercepts add-to-cart to apply it.
 *
 * Shopify holds one cart discount code at a time, hence one bracelet per order.
 */

// pwyw-discounts.js is keyed 0..99, so full price maps to no code — there is nothing to discount.
const FULL_PRICE_AMOUNT = 100;

const PWYW_DISCOUNT_PREFIX = 'PWYW_';

// ---------------------------------------------------------------------------
// Amount validation
// ---------------------------------------------------------------------------

// Bounds come from the field's attributes, so Liquid stays the source of truth for the ceiling.
function isValidAmount(value, input) {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  const amount = Number(value);
  const max = Number(input.getAttribute('max'));
  const min = Number(input.getAttribute('min')) || 1;

  if (max > 0 && amount > max) {
    return false;
  }

  return amount >= min;
}

function onKeyChange(event) {
  const value = event.target.value;
  const errorElement = document.querySelector('.pwyw-input-error');
  const pwywInputElement = document.querySelector('#pwyw-amount');
  const pwywParentElement = pwywInputElement.parentElement;

  // Empty is "not filled in yet", not wrong — only the add handler refuses it.
  const hasError = value !== '' && !isValidAmount(value, pwywInputElement);

  errorElement.hidden = !hasError;

  const optionButtons = document.querySelectorAll('.pwyw-suggestion-button');
  optionButtons.forEach((button) => {
    button.classList.remove('--is-selected');
  });

  if (value) {
    pwywParentElement.classList.add('--has-value');
  } else {
    pwywParentElement.classList.remove('--has-value');
  }
}

function onAddToCartSuggestedPrice(event) {
  const button = event.currentTarget;
  button.classList.add('--is-selected');
  const suggestedPrice = button.getAttribute('data-amount');
  const pwywInputElement = document.querySelector('#pwyw-amount');
  pwywInputElement.value = suggestedPrice;
  pwywInputElement.parentElement.classList.add('--has-value');

  const errorElement = document.querySelector('.pwyw-input-error');
  if (errorElement) {
    errorElement.hidden = true;
  }

  const optionButtons = document.querySelectorAll('.pwyw-suggestion-button');
  optionButtons.forEach((btn) => {
    if (btn !== button) {
      btn.classList.remove('--is-selected');
    }
  });
}

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

function readCartResponse(response) {
  return response.json().then((data) => {
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Cart request failed.');
    }
    return data;
  });
}

function postCart(url, body) {
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .then(readCartResponse);
}

/**
 * Stores a discount code on the cart.
 *
 * Resolving does NOT mean the code applied — Shopify accepts an unusable code and reports
 * `applicable: false`. Callers must verify against the line. It lands even on a cart holding
 * nothing it applies to, which is what makes it safe to call before the add so UpCart's drawer
 * never paints a full price.
 */
function applyDiscountCode(code) {
  // Never reject: a failure here must not stop the shopper adding the bracelet.
  return postCart('/cart/update.js', { discount: code }).catch(() => null);
}

/**
 * Fallback route, setting the discount cookie instead of writing to the cart — an independent
 * mechanism, so it can work where the other silently does not. Not the primary because it 401s
 * behind `shopify theme dev`'s proxy, which would make local testing impossible.
 */
function applyDiscountViaRedirect(code) {
  return window
    .fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    .then((response) => response.json())
    .catch(() => null);
}

/**
 * Posts the FormData as-is rather than as JSON: the form carries bracket-notation keys
 * (`properties[Preorder]`, `attributes[is_pwyw_bracelet_id]`) that Shopify only parses from an
 * encoded body. JSON would send them as flat keys and silently drop the cart attribute the
 * one-per-order check depends on.
 */
function addFormToCart(form) {
  return window
    .fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    })
    .then(readCartResponse);
}

function retryDiscountCode(code) {
  return applyDiscountViaRedirect(code).then((cart) => cart || getCart());
}

/** Shopify's own verdict on a code it accepted: stored, but could it actually be used. */
function discountCodeStatus(cart, code) {
  const entry =
    cart && cart.discount_codes ? cart.discount_codes.find((candidate) => candidate.code === code) : null;
  if (!entry) {
    return 'not stored on the cart';
  }
  return entry.applicable ? 'applicable' : 'stored but not applicable';
}

function isPwywTitle(title) {
  return typeof title === 'string' && title.indexOf(PWYW_DISCOUNT_PREFIX) === 0;
}

// Reads both shapes the Ajax API exposes — `discounts` is legacy, `line_level_discount_allocations`
// current — so detection does not depend on which one a given Shopify version returns.
function lineHasPwywDiscount(item) {
  if (item.discounts && item.discounts.some((discount) => isPwywTitle(discount.title))) {
    return true;
  }

  return Boolean(
    item.line_level_discount_allocations &&
      item.line_level_discount_allocations.some(
        (allocation) => allocation.discount_application && isPwywTitle(allocation.discount_application.title),
      ),
  );
}

/** Lines carrying a PWYW discount, or matching the cart attribute set by buy-buttons.liquid. */
function findPwywLines(cart) {
  if (!cart || !cart.items) {
    return [];
  }

  const pwywProductId = cart.attributes ? cart.attributes.is_pwyw_bracelet_id : null;

  return cart.items.filter((item) => {
    // The product-id fallback covers a line whose discount failed to apply: matching on the
    // discount title alone would report "no PWYW line" and let a second bracelet through.
    return lineHasPwywDiscount(item) || (pwywProductId && String(item.product_id) === String(pwywProductId));
  });
}

// Looks at the cart's codes rather than its lines: a code stranded by a removed line applies to
// nothing, so the line items no longer mention it.
function hasPwywDiscountCode(cart) {
  if (!cart) {
    return false;
  }

  if (cart.discount_codes && cart.discount_codes.some((entry) => isPwywTitle(entry.code))) {
    return true;
  }

  return Boolean(
    cart.cart_level_discount_applications &&
      cart.cart_level_discount_applications.some((application) => isPwywTitle(application.title)),
  );
}

/**
 * Drops a PWYW code once its line is gone.
 *
 * Only ever clears a code we recognise as ours: `{discount: ''}` wipes whatever is on the cart, so
 * clearing unconditionally would delete a promo code the shopper entered themselves.
 */
function clearStalePwywDiscount(cart) {
  if (findPwywLines(cart).length || !hasPwywDiscountCode(cart)) {
    return Promise.resolve(cart);
  }

  return postCart('/cart/update.js', { discount: '' }).catch((error) => {
    console.error('Unable to clear stale PWYW discount:', error);
    return cart;
  });
}

// ---------------------------------------------------------------------------
// PDP
// ---------------------------------------------------------------------------

function PWYWBracelet() {
  const pwywForm = document.querySelector('.pwyw-form');
  if (!pwywForm) {
    return;
  }

  // Resolve outwards from the PWYW field, not from the top of the document: card-product.liquid
  // also renders <product-form>, so a document-wide query can match a product card instead of this
  // PDP's form and bind the interceptor to the wrong one.
  const productInfo = pwywForm.closest('product-info') || document.querySelector('product-info');
  const productForm = productInfo ? productInfo.querySelector('product-form') : null;

  const form = productForm ? productForm.querySelector('form') : null;
  const submitButton = productInfo ? productInfo.querySelector('.product-form__submit') : null;
  const notAvailableButton = productInfo
    ? productInfo.querySelector('.product-form__unavailable-button-pwyw')
    : null;
  const pwywInput = document.querySelector('#pwyw-amount');
  const errorElement = document.querySelector('.pwyw-input-error');
  const suggestionButtons = document.querySelectorAll('.pwyw-suggestion-button');

  let isSubmitting = false;

  // Loud on purpose: without the interceptor the page still adds to cart, just at full price.
  if (!form || !submitButton || !pwywInput) {
    console.error('PWYW: could not resolve the product form. Add to cart would charge full price.', {
      productInfo: Boolean(productInfo),
      productForm: Boolean(productForm),
      form: Boolean(form),
      submitButton: Boolean(submitButton),
      pwywInput: Boolean(pwywInput),
    });
    return;
  }

  /**
   * Read from the button's label rather than its `disabled` attribute, which this module also
   * sets. Dawn rewrites the label on every variant change (global.js `toggleAddButton`), so it is
   * the only availability signal that stays true after the shopper switches bead colour.
   */
  function isPurchasable() {
    const label = submitButton.querySelector('span');
    if (!label || !window.variantStrings) {
      return true;
    }
    return label.textContent.trim() === window.variantStrings.addToCart.trim();
  }

  function setBusy(busy) {
    const spinner = submitButton.querySelector('.loading__spinner');
    submitButton.classList.toggle('loading', busy);
    if (spinner) {
      spinner.classList.toggle('hidden', !busy);
    }
    if (busy) {
      submitButton.setAttribute('disabled', true);
    } else if (isPurchasable()) {
      submitButton.removeAttribute('disabled');
    }
  }

  function showError() {
    if (errorElement) {
      errorElement.hidden = false;
    }
    pwywInput.setAttribute('aria-invalid', 'true');
    pwywInput.focus();
  }

  /** Toggles the PDP between "buy" and "already have one" and drops any orphaned code. */
  function handleBraceletPdpState(cart) {
    if (!cart) {
      return;
    }

    const pwywInCart = findPwywLines(cart).length > 0;

    submitButton.classList.toggle('hidden', pwywInCart);
    if (notAvailableButton) {
      notAvailableButton.classList.toggle('hidden', !pwywInCart);
    }

    if (pwywInCart || !isPurchasable()) {
      submitButton.setAttribute('disabled', true);
    } else {
      submitButton.removeAttribute('disabled');
    }

    suggestionButtons.forEach((button) => {
      button.disabled = pwywInCart;
    });
    pwywInput.disabled = pwywInCart;

    clearStalePwywDiscount(cart);
  }

  function refreshState() {
    return getCart().then(handleBraceletPdpState);
  }

  /**
   * Adds the bracelet with its discount code. The only path that may add a PWYW line.
   *
   * Guarded against re-entry: the button stays visible while the request is in flight, and a
   * double click would otherwise add two bracelets against a one-per-order product.
   */
  function runPwywAdd() {
    if (isSubmitting) {
      return;
    }

    const value = pwywInput.value.trim();
    if (value === '' || !isValidAmount(value, pwywInput)) {
      showError();
      return;
    }

    pwywInput.removeAttribute('aria-invalid');
    if (errorElement) {
      errorElement.hidden = true;
    }

    const amount = Number(value);
    // Full price has no code by design, so a missing one there is success, not failure.
    const discountCode = amount === FULL_PRICE_AMOUNT ? null : pwywDiscounts[amount];

    if (!discountCode && amount !== FULL_PRICE_AMOUNT) {
      showError();
      return;
    }

    isSubmitting = true;
    setBusy(true);

    // Discount before add, so UpCart's drawer opens on an already-discounted cart rather than
    // flashing the full price.
    const discountApplied = discountCode ? applyDiscountCode(discountCode) : Promise.resolve(null);

    discountApplied
      .then(() => addFormToCart(form))
      .then(() => getCart())
      .then((cart) => {
        // Storing a code is not applying it, so verify against the line and retry via the other
        // route — the two are independent mechanisms and either can be the one that works.
        if (!discountCode || findPwywLines(cart).some(lineHasPwywDiscount)) {
          return cart;
        }
        return retryDiscountCode(discountCode);
      })
      .then((cart) => {
        if (discountCode && !findPwywLines(cart).some(lineHasPwywDiscount)) {
          // Report Shopify's verdict: "stored but not applicable" means the request worked and the
          // fault is the code's own configuration — deactivated, wrong product scope, dates.
          console.error(
            `PWYW discount ${discountCode} did not apply — ${discountCodeStatus(cart, discountCode)}.`,
          );
        }
        return cart;
      })
      .then(() => {
        // UpCart normally opens itself on /cart/add.js; this is a guarded fallback for when it
        // does not, since the API is undocumented.
        if (typeof window.upcartToggleCart === 'function') {
          window.upcartToggleCart();
        }
        isSubmitting = false;
        setBusy(false);
        return refreshState();
      })
      .catch((error) => {
        isSubmitting = false;
        setBusy(false);
        console.error('PWYW add to cart failed:', error);
        const messageWrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const message = productForm.querySelector('.product-form__error-message');
        if (messageWrapper && message) {
          message.textContent = error.message;
          messageWrapper.removeAttribute('hidden');
        }
      });
  }

  /**
   * The click, not the submit: UpCart binds its own click handler to the add button and calls
   * /cart/add.js itself, so the form's `submit` event never fires and a submit-phase interceptor
   * is never reached. Capture on `document` runs before any listener on the button, and
   * stopImmediatePropagation stops UpCart adding the line undiscounted.
   */
  function onPwywAddClick(event) {
    const button = event.target.closest('[name="add"]');
    if (!button || !form.contains(button)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    runPwywAdd();
  }

  // Covers Enter-to-submit, which fires no click, and the case where UpCart is disabled and Dawn's
  // product-form handles the form again.
  function onPwywSubmit(event) {
    if (event.target !== form) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    runPwywAdd();
  }

  document.addEventListener('click', onPwywAddClick, true);
  document.addEventListener('submit', onPwywSubmit, true);

  // Paint from the cart before waiting on UpCart, so a slow or absent UpCart degrades to "no live
  // sync" rather than leaving the PDP unbuyable for the full 10s timeout.
  refreshState()
    .then(() => waitForUpCartShadowRoot())
    .then(() => {
      monitorCartChange({
        handleCartCallback: getCart,
        handleUIUpdate: handleBraceletPdpState,
        // Disable immediately on every mutation — before the debounced fetch resolves
        onMutationStart: () => submitButton.setAttribute('disabled', true),
      });
    })
    .catch((error) => {
      console.error(error.message);
    });
}

// Hooked on the form itself rather than a template suffix, so it survives PWYW moving from its own
// section into a product-bracelet-overview block and still covers the archived template.
if (document.querySelector('.pwyw-form')) {
  PWYWBracelet();

  const pwywInputElement = document.querySelector('#pwyw-amount');
  const optionButtons = document.querySelectorAll('.pwyw-suggestion-button');
  optionButtons.forEach((button) => {
    button.addEventListener('click', onAddToCartSuggestedPrice);
  });

  if (pwywInputElement) {
    pwywInputElement.addEventListener('input', onKeyChange);
  }
}
